import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Proposicao } from '../types/index.js';
import './SimilaresVariants.css';

interface Similar {
  proposicao: Proposicao;
  score: string;
}

interface SimilaresVariantsProps {
  similares: Similar[];
  variant?: 'v1' | 'v2' | 'v3';
  currentProposicao?: Proposicao;
}

const tipoColors: Record<string, { bg: string; color: string }> = {
  PL: { bg: '#fef4a8', color: '#d9960c' },
  PEC: { bg: '#e8d5f2', color: '#7c4dff' },
  PLP: { bg: '#f3e5ff', color: '#7c4dff' },
  MPV: { bg: '#ffebee', color: '#c0392b' },
  PDL: { bg: '#e3f2fd', color: '#1976d2' },
};

function V1List({ similares }: { similares: Similar[] }) {
  const navigate = useNavigate();

  return (
    <div className="similares-v1">
      {similares.map((sim) => {
        const score = parseFloat(sim.score);
        return (
          <div key={sim.proposicao.id} className="similar-item-v1">
            <div className="similar-card-v1" onClick={() => navigate(`/detail/${sim.proposicao.id}`)}>
              <div className="similar-header">
                <span
                  className="similar-badge"
                  style={{
                    backgroundColor: tipoColors[sim.proposicao.tipo]?.bg,
                    color: tipoColors[sim.proposicao.tipo]?.color,
                  }}
                >
                  {sim.proposicao.tipo}
                </span>
                <span className="similar-titulo">{sim.proposicao.titulo}</span>
                <span className="similar-score">{Math.round(score * 100)}%</span>
              </div>
              <div
                className="similar-progress-bar"
                style={{
                  width: `${score * 100}%`,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function V2Graph({ similares, currentProposicao }: { similares: Similar[]; currentProposicao?: Proposicao }) {
  const navigate = useNavigate();

  const cx = 200;
  const cy = 200;
  const maxRadius = 150;

  return (
    <div className="similares-v2">
      <svg viewBox="0 0 400 400" className="similares-graph">
        {/* Lines from center to nodes */}
        {similares.map((sim, idx) => {
          const angle = (idx / similares.length) * Math.PI * 2;
          const x = cx + Math.cos(angle) * maxRadius;
          const y = cy + Math.sin(angle) * maxRadius;
          const score = parseFloat(sim.score);

          return (
            <line
              key={`line-${sim.proposicao.id}`}
              x1={cx}
              y1={cy}
              x2={x}
              y2={y}
              stroke={tipoColors[sim.proposicao.tipo]?.color || '#999'}
              strokeWidth="2"
              opacity={0.3 + score * 0.7}
            />
          );
        })}

        {/* Center node */}
        {currentProposicao && (
          <>
            <circle cx={cx} cy={cy} r="20" fill={tipoColors[currentProposicao.tipo]?.bg} stroke={tipoColors[currentProposicao.tipo]?.color} strokeWidth="2" />
            <text x={cx} y={cy + 5} textAnchor="middle" fontSize="10" fontWeight="bold" fill={tipoColors[currentProposicao.tipo]?.color}>
              {currentProposicao.tipo}
            </text>
          </>
        )}

        {/* Similar nodes */}
        {similares.map((sim, idx) => {
          const angle = (idx / similares.length) * Math.PI * 2;
          const score = parseFloat(sim.score);
          const nodeRadius = 12 + score * 20;
          const x = cx + Math.cos(angle) * maxRadius;
          const y = cy + Math.sin(angle) * maxRadius;

          return (
            <g
              key={sim.proposicao.id}
              onClick={() => navigate(`/detail/${sim.proposicao.id}`)}
              style={{ cursor: 'pointer' }}
            >
              <circle
                cx={x}
                cy={y}
                r={nodeRadius}
                fill={tipoColors[sim.proposicao.tipo]?.bg}
                stroke={tipoColors[sim.proposicao.tipo]?.color}
                strokeWidth="2"
                opacity="0.8"
              />
              <text x={x} y={y - nodeRadius - 12} textAnchor="middle" fontSize="10" fontWeight="bold">
                {sim.proposicao.tipo} {sim.proposicao.numero}
              </text>
              <text x={x} y={y + nodeRadius + 14} textAnchor="middle" fontSize="9" fill="#666">
                {Math.round(score * 100)}%
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function V3Clusters({ similares }: { similares: Similar[] }) {
  const navigate = useNavigate();

  // Group by tipo
  const clusters = useMemo(() => {
    const grouped: Record<string, Similar[]> = {};

    similares.forEach((sim) => {
      if (!grouped[sim.proposicao.tipo]) {
        grouped[sim.proposicao.tipo] = [];
      }
      grouped[sim.proposicao.tipo].push(sim);
    });

    // Calculate average score per cluster and sort
    return Object.entries(grouped)
      .map(([tipo, items]) => ({
        tipo,
        items: items.sort((a, b) => parseFloat(b.score) - parseFloat(a.score)),
        avgScore: items.reduce((sum, sim) => sum + parseFloat(sim.score), 0) / items.length,
      }))
      .sort((a, b) => b.avgScore - a.avgScore);
  }, [similares]);

  return (
    <div className="similares-v3">
      {clusters.map((cluster) => (
        <div key={cluster.tipo} className="cluster-group">
          <div className="cluster-header">
            <span className="cluster-badge" style={{ backgroundColor: tipoColors[cluster.tipo]?.bg, color: tipoColors[cluster.tipo]?.color }}>
              {cluster.tipo}
            </span>
            <span className="cluster-count">{cluster.items.length} proposição(ões)</span>
            <div className="cluster-avg-bar">
              <div
                className="cluster-avg-fill"
                style={{
                  width: `${cluster.avgScore * 100}%`,
                  backgroundColor: tipoColors[cluster.tipo]?.color,
                }}
              />
            </div>
          </div>
          <div className="cluster-items">
            {cluster.items.map((sim) => {
              const score = parseFloat(sim.score);
              return (
                <div
                  key={sim.proposicao.id}
                  className="cluster-item"
                  onClick={() => navigate(`/detail/${sim.proposicao.id}`)}
                >
                  <span className="cluster-item-numero">
                    {sim.proposicao.tipo} {sim.proposicao.numero}/{sim.proposicao.ano}
                  </span>
                  <span className="cluster-item-titulo">{sim.proposicao.titulo}</span>
                  <span className="cluster-item-score">{Math.round(score * 100)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SimilaresVariants({ similares, variant = 'v1', currentProposicao }: SimilaresVariantsProps) {
  if (!similares || similares.length === 0) {
    return <div className="similares-empty">Nenhuma proposição similar encontrada</div>;
  }

  if (variant === 'v2') {
    return <V2Graph similares={similares} currentProposicao={currentProposicao} />;
  }

  if (variant === 'v3') {
    return <V3Clusters similares={similares} />;
  }

  // Default: V1
  return <V1List similares={similares} />;
}
